import { useMutation, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { api } from "../../api/client.ts";
import { keys } from "../../api/queryKeys.ts";
import { plural } from "../../components/review/rules.ts";
import type {
  DoneVars,
  ReviewActions,
  ReviewCopy,
  ReviewVars,
  SnoozeVars,
} from "../../components/review/types.ts";
import { formatDate } from "../../utils/format.ts";

/** How the review controls read on a tenant's prospect. */
export const PROSPECT_REVIEW_COPY: ReviewCopy = {
  noun: "prospect",
  notReviewable: "Nothing to decide on yet",
  notOnWorklist: "Pursue it first: Done takes a prospect off the worklist",
  feedbackHelp: "Goes to the agents that found this company",
};

/**
 * The bulk-capable actions on prospects; each refreshes every prospect query
 * and the lists (a decision changes the company's entries on them).
 */
export function useProspectReview(onDone?: () => void): ReviewActions {
  const queryClient = useQueryClient();
  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: keys.prospect.all });
    queryClient.invalidateQueries({ queryKey: keys.prospectList.all });
    queryClient.invalidateQueries({ queryKey: keys.signal.all });
    onDone?.();
  };

  const continueSteps = useMutation({
    mutationFn: (prospectIds: string[]) => api.continueProspects(prospectIds),
    onSuccess: ({ results }) => {
      const approved = results.reduce((n, r) => n + r.approved, 0);
      const skipped = results.filter((r) => r.approved === 0);
      if (approved === 0) {
        toast.error(skipped[0]?.skipped[0] ?? "Nothing to continue");
      } else {
        toast.success(
          `Continued ${plural(approved, "step")}` +
            (skipped.length ? `, ${plural(skipped.length, "prospect")} left waiting` : ""),
        );
      }
      refresh();
    },
    onError: refresh,
  });

  const review = useMutation({
    mutationFn: (vars: ReviewVars) =>
      api.reviewProspects({
        prospect_ids: vars.ids,
        action: vars.action,
        reason: vars.reason ?? null,
        snooze_until: vars.snoozeUntil ?? null,
      }),
    onSuccess: ({ results }, vars) => {
      const done = results.filter((r) => !r.skipped);
      const skipped = results.filter((r) => r.skipped);
      const verb = { accept: "Pursuing", disqualify: "Disqualified", reset: "Reset" }[vars.action];
      if (done.length === 0) {
        toast.error(skipped[0]?.skipped ?? "Nothing to review");
      } else {
        toast.success(
          `${verb} ${plural(done.length, "prospect")}` +
            (vars.snoozeUntil ? `, snoozed until ${formatDate(vars.snoozeUntil)}` : "") +
            (skipped.length ? `, ${skipped.length} left as they were` : ""),
        );
      }
      refresh();
    },
    onError: refresh,
  });

  const snooze = useMutation({
    mutationFn: (vars: SnoozeVars) => api.snoozeProspects(vars.ids, vars.until),
    onSuccess: ({ updated, skipped }, vars) => {
      if (vars.until && updated.length === 0) {
        toast.error("Only prospects on the worklist can be snoozed");
      } else {
        toast.success(
          (vars.until
            ? `Snoozed ${plural(updated.length, "prospect")}`
            : `Snooze taken off ${plural(updated.length, "prospect")}`) +
            (skipped.length ? `, ${skipped.length} not on the worklist` : ""),
        );
      }
      refresh();
    },
    onError: refresh,
  });

  const done = useMutation({
    mutationFn: (vars: DoneVars) => api.markProspectsDone(vars.ids, vars.done),
    onSuccess: ({ updated }, vars) => {
      toast.success(
        vars.done
          ? `Marked ${plural(updated.length, "prospect")} done`
          : `Reopened ${plural(updated.length, "prospect")}`,
      );
      refresh();
    },
    onError: refresh,
  });

  return {
    continueSteps,
    review,
    snooze,
    done,
    busy: continueSteps.isPending || review.isPending || snooze.isPending || done.isPending,
  };
}
